// Full-document render of the tempurpedic ContactFormPage LANDING ("thank
// you") state, shown after a successful submission (and via the admin
// "landing" preview / the puppeteer `--submitted` fixture). Identical shell
// to ContactFormPageTemplate -- header_content stream, the contact section
// ("Contact Us" <h1> + rich-text intro), the info aside (+ the kept ChatCard
// island host div), footer_content stream -- but the section holds the
// thank-you rich text instead of the form, and the aside phone renders raw
// as_national in a <p class="type_x0point875"> (the form page hyphenates
// inside an <h3> -- a real divergence between the two, preserved).
import React from "react";
import { t } from "ttag";

import { templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { buildToolboxPageMap } from "../blocks/toolboxPageMap";
import { ChatCard } from "../components/ChatCard";
import { ContactUsAside } from "../components/ContactUsAside";
import { PageSkeleton } from "../components/PageSkeleton";

// Mutable reactivated props (not Immutable<>): this template composes
// PageSkeleton, which is typed with mutable reactivated props (see
// PageSkeleton).
type IProps = templates.ContactFormPageLandingTemplate;

export const Template = (props: IProps): React.JSX.Element => {
    const toolboxMap = buildToolboxPageMap({
        hardcoded_ssr: props.hardcoded_ssr,
        hotspot_ssr: props.hotspot_ssr,
        breadcrumbs_ssr: props.breadcrumbs_ssr,
        customer_service_ssr: props.customer_service_ssr,
        shop_compare_ssr: props.shop_compare_ssr,
        tab_feature_attribute_ssr: props.tab_feature_attribute_ssr,
        reviews_ssr: props.reviews_ssr,
    });
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {/* React 19 hoists this into <head>. */}
            <meta name="chatTags" content="contact" />
            <div className="l-capped-width">
                <StreamField
                    tagName={React.Fragment}
                    value={props.page.header_content.value}
                    components={toolboxMap}
                />

                <div className="contact-us-container">
                    <section>
                        <header>
                            <h1>{t`Contact Us`}</h1>
                        </header>

                        <div className="intro-text">
                            <div
                                className="rich-text"
                                dangerouslySetInnerHTML={{
                                    __html: props.intro_html,
                                }}
                            />
                        </div>

                        <div
                            className="rich-text"
                            dangerouslySetInnerHTML={{
                                __html: props.thank_you_html,
                            }}
                        />
                    </section>

                    <aside>
                        <ContactUsAside
                            aside={props.aside}
                            phoneVariant="small"
                        />
                        {/* The ChatCard island's host div is load-bearing:
                            contact-us.scss lays the aside out per `aside > div`
                            child (grid columns, icon sizing), so the wrapper
                            div stays now the island is inlined. */}
                        <div>
                            <ChatCard />
                        </div>
                    </aside>
                </div>

                <StreamField
                    tagName={React.Fragment}
                    value={props.page.footer_content.value}
                    components={toolboxMap}
                />
            </div>
        </PageSkeleton>
    );
};
